import type { IconProps } from '@phosphor-icons/react';
import type { MutableRefObject } from 'react';
import ky, { type HTTPError } from 'ky';
import qs from 'qs';

import type { AnyPage, ArticlePage, IconApiResponse } from '@tmh-website/shared/cms';
import type { ApiErrorResponse } from '@tmh-website/services/cms.service';
import type { LocalePrefix, Project } from '@tmh-website/shared/const';
import type {
  GetArticlesCompactByGroupCache,
  GetArticlesCompactByGroupOptions,
  GetArticlesCompactOptions,
} from '@tmh-website/services/article.cms.service';
import {
  universalGetArticlesCompact,
  universalGetArticlesCompactByGroup,
  universalGetRelatedArticles,
} from '@tmh-website/services/article.cms.service';
import {
  customParseJson,
  universalGetContactSupport,
  universalGetGlobalData,
  universalGetPartners,
} from '@tmh-website/services/cms.service';

export const cmsWebClient = ky.extend({
  mode: 'cors',
  prefixUrl: process.env.CMS_BASE_URL ?? process.env.NX_CMS_BASE_URL ?? process.env.NEXT_PUBLIC_CMS_BASE_URL,
  hooks: {
    afterResponse: [
      /**
       * This hook parses the JSON response, even if the response failed.
       * This is important if we want to display an server provided error message to the user.
       */
      async (_req, _options, res) => {
        if (!res.ok) {
          (res as ApiErrorResponse).payload = JSON.parse(await res.text());
          return res;
        } else {
          return res;
        }
      },
    ],
  },
  parseJson: input => {
    return JSON.parse(input, customParseJson);
  },
});

export const nextWebClient = ky.extend({
  mode: 'cors',
  prefixUrl: (process.env.NEXT_PUBLIC_ORIGIN ?? process.env.NX_NEXT_PUBLIC_ORIGIN) + '/web-v2/api',
});

/**
 * Gets a compact version of all articles to save bandwidth
 * You can either pass a type to get the first X articles of that type
 * or a group to get the first X articles of each type of that group
 */
export async function getArticlesCompact(options: GetArticlesCompactOptions) {
  return universalGetArticlesCompact(cmsWebClient, options);
}

/**
 * Gets a compact version of articles from multiple collections to save bandwidth
 */
export async function getArticlesCompactByGroup(
  options: GetArticlesCompactByGroupOptions,
  cache?: MutableRefObject<GetArticlesCompactByGroupCache | undefined>
) {
  return universalGetArticlesCompactByGroup(cmsWebClient, options, cache);
}

/**
 * Get related articles for a given article.
 */
export async function getRelatedArticles(options: GetArticlesCompactOptions, page: ArticlePage, amount?: number) {
  return universalGetRelatedArticles(cmsWebClient, options, page, amount);
}

/**
 * Get partners data from Strapi
 */
export async function getPartners(locale: LocalePrefix, page: AnyPage, project?: Project) {
  return await universalGetPartners(cmsWebClient, { locale, page, project });
}

/**
 * Fetch global data from Strapi
 */
export async function getGlobalData(project: Project, locale: LocalePrefix) {
  return await universalGetGlobalData(cmsWebClient, { project, locale });
}

/**
 * Get the contact & support data from Strapi
 */
export async function getContactSupport(locale: LocalePrefix) {
  return await universalGetContactSupport(cmsWebClient, { locale });
}

export function getMessageFromApiError(error: HTTPError) {
  return (error.response as ApiErrorResponse)?.payload?.error?.message ?? 'Unknown error';
}

/**
 * Turn a icon id into a svg object.
 * Used for client side rendering only since server side icons should be loaded during SSR.
 */
export function getIconSvgObjectById(iconId: string, props?: IconProps) {
  return nextWebClient
    .get('icons', {
      searchParams: qs.stringify({
        iconId,
        props,
      }),
    })
    .json<IconApiResponse>();
}
